import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Eye, Bell, Loader2, MessageCircle } from "lucide-react";
import { Switch } from "../ui/switch";
import { aiApi } from "./aiApi";

const ROLES = [
  ["admin", "Penyelia"],
  ["branch_manager", "Pengurus Cawangan"],
  ["guru", "Guru / Pendidik"],
  ["ibu_bapa", "Ibu Bapa"],
];

/** Jadual ringkas akses Mimi ikut peranan: butang terapung + push notifikasi + suis WhatsApp AI. */
export default function AIBubbleVisibility() {
  const [roles, setRoles] = useState(null);
  const [push, setPush] = useState({});
  const [saving, setSaving] = useState(null);
  const [waOn, setWaOn] = useState(true);

  useEffect(() => {
    aiApi.bubbleVisibility().then((d) => { setRoles(d.roles); setPush(d.push_roles || {}); setWaOn(d.whatsapp_ai_enabled !== false); })
      .catch(() => toast.error("Gagal memuat tetapan paparan Mimi Junior"));
  }, []);

  const toggleBubble = async (role, label, value) => {
    setSaving(`b-${role}`);
    const prev = roles;
    setRoles((r) => ({ ...r, [role]: value }));
    try {
      const d = await aiApi.saveBubbleVisibility({ [role]: value });
      setRoles(d.roles);
      toast.success(`Mimi Junior ${value ? "dipaparkan" : "disembunyikan"} untuk ${label}`);
    } catch (e) { setRoles(prev); toast.error(e.response?.data?.detail || "Gagal menyimpan"); }
    finally { setSaving(null); }
  };

  const togglePush = async (role, label, value) => {
    setSaving(`p-${role}`);
    const prev = push;
    setPush((r) => ({ ...r, [role]: value }));
    try {
      const d = await aiApi.savePushRoles({ [role]: value });
      setPush(d.push_roles);
      toast.success(`Push notifikasi peringatan ${value ? "dihidupkan" : "dimatikan"} untuk ${label}`);
    } catch (e) { setPush(prev); toast.error(e.response?.data?.detail || "Gagal menyimpan"); }
    finally { setSaving(null); }
  };

  const toggleWA = async (value) => {
    setSaving("wa");
    setWaOn(value);
    try {
      const d = await aiApi.saveWhatsappAI(value);
      setWaOn(d.whatsapp_ai_enabled !== false);
      toast.success(`WhatsApp AI ${value ? "dihidupkan" : "dimatikan"}`);
    } catch (e) { setWaOn(!value); toast.error(e.response?.data?.detail || "Gagal menyimpan"); }
    finally { setSaving(null); }
  };

  if (!roles) return <div className="grid place-items-center h-16"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>;

  return (
    <div className="rounded-xl bg-slate-50/70 border border-slate-100 p-2.5" data-testid="ai-bubble-visibility">
      <div className="grid grid-cols-[1fr_64px_64px] items-center px-1 pb-1.5 text-[10px] font-bold uppercase tracking-wide text-slate-400">
        <span>Peranan</span>
        <span className="flex items-center justify-center gap-1"><Eye className="h-3 w-3" /> Butang</span>
        <span className="flex items-center justify-center gap-1"><Bell className="h-3 w-3" /> Push</span>
      </div>
      <div className="rounded-lg bg-white border border-slate-100 divide-y divide-slate-50" data-testid="ai-push-roles">
        {ROLES.map(([k, label]) => (
          <div key={k} className="grid grid-cols-[1fr_64px_64px] items-center px-2 py-2">
            <span className="text-[13px] text-slate-700 truncate">{label}</span>
            <span className="flex justify-center" data-testid={`ai-bubble-vis-status-${k}`} data-on={roles[k] !== false}>
              <Switch checked={roles[k] !== false} disabled={saving === `b-${k}`} onCheckedChange={(v) => toggleBubble(k, label, v)} data-testid={`ai-bubble-vis-switch-${k}`} />
            </span>
            <span className="flex justify-center" data-testid={`ai-push-status-${k}`} data-on={push[k] !== false}>
              <Switch checked={push[k] !== false} disabled={saving === `p-${k}`} onCheckedChange={(v) => togglePush(k, label, v)} data-testid={`ai-push-switch-${k}`} />
            </span>
          </div>
        ))}
      </div>
      <div className="text-[10.5px] text-slate-400 px-1 pt-1.5">Superadmin sentiasa nampak butang & terima push. Peringatan tetap disimpan dalam Peti Masuk AI.</div>

      <div className="mt-2.5 rounded-lg bg-white border border-slate-100 px-2 py-2" data-testid="ai-whatsapp-master">
        <div className="flex items-center gap-2">
          <MessageCircle className={`h-4 w-4 ${waOn ? "text-emerald-600" : "text-slate-400"}`} />
          <div className="flex-1 text-[13px] font-bold text-slate-700">Fungsi WhatsApp AI</div>
          <span className={`text-[11px] font-bold ${waOn ? "text-emerald-600" : "text-rose-500"}`} data-testid="ai-whatsapp-master-status">{waOn ? "Hidup" : "Mati"}</span>
          <Switch checked={waOn} disabled={saving === "wa"} onCheckedChange={toggleWA} data-testid="ai-whatsapp-master-switch" />
        </div>
        <div className="text-[10.5px] text-slate-400 mt-1">Bila mati: tiada laporan, amaran & balasan WhatsApp. Peringatan dalam app tetap berjalan.</div>
      </div>
    </div>
  );
}
